import type { FormDefinition, FormStep } from '@/api/types/Forms'
import { differenceInYears } from 'date-fns'
import { type FormStates, getDateState, isDateComplete } from './stepState'

/**
 * Whether a step is shown, given what has been answered so far.
 *
 * A step with no condition is always shown. An age condition needs a complete date behind it;
 * without one the step stays hidden rather than guessing.
 */
export const isStepVisible = (
	step: FormStep,
	states: FormStates,
	now = new Date()
): boolean => {
	const showIf = step.showIf
	if (!showIf) {
		return true
	}

	switch (showIf.type) {
		case 'AGE_UNDER': {
			const date = getDateState(states[showIf.stepId])
			if (!isDateComplete(date, now)) {
				return false
			}
			const birthDate = new Date(
				Number.parseInt(date.year, 10),
				Number.parseInt(date.month, 10) - 1,
				Number.parseInt(date.day, 10)
			)
			return differenceInYears(now, birthDate) < showIf.value
		}
		default:
			return true
	}
}

/**
 * Whether this step's answer is a date of birth, which is true when something else in the form
 * reads an age off it.
 *
 * Asked so the field can offer the system's own birth date. Taken from the form rather than from
 * the step's label: a label is a name, and a form that happens to be worded differently would
 * quietly stop autofilling, whereas a step an age condition points at is a birth date by
 * definition.
 */
export const isBirthDateStep = (definition: FormDefinition, stepId: number): boolean =>
	definition.steps.some(
		(step) => step.showIf?.type === 'AGE_UNDER' && step.showIf.stepId === stepId
	)
